import { io, type Socket } from 'socket.io-client';
import { dispatchStorefrontUpdated } from './storefrontEvents';
import { dispatchClientOrdersUpdated, type ClientOrdersUpdatedDetail } from './orderEvents';
import { dispatchTenantOrdersUpdated } from './tenantOrderEvents';
import { dispatchAnnouncementsUpdated } from './announcementEvents';
import { dispatchTenantPlanUpdated } from './tenantPlanEvents';
import { dispatchClientUpdated, type ClientUpdatedDetail } from './clientEvents';
import { dispatchVisitingCardUpdated } from './visitingCardEvents';
import { dispatchGallerySelectionCreated, dispatchGalleryUpdated } from './galleryEvents';

let socket: Socket | null = null;
let activeTenantId: string | null = null;
let activeClientId: string | null = null;
let activeJoinAdmin = false;

function joinRooms(s: Socket) {
  if (activeTenantId) {
    s.emit('join_tenant_public', activeTenantId);
    if (activeJoinAdmin) {
      s.emit('join_tenant_admin', activeTenantId);
    }
  }
  if (activeClientId) {
    s.emit('join_client', activeClientId);
  }
}

function ensureSocket(): Socket {
  if (!socket) {
    socket = io({
      path: '/socket.io',
      autoConnect: false,
      transports: ['websocket', 'polling'],
    });
    socket.on('storefront_updated', () => {
      dispatchStorefrontUpdated();
    });
    socket.on('visiting_card_updated', () => {
      dispatchVisitingCardUpdated();
    });
    socket.on('client_updated', (payload: ClientUpdatedDetail) => {
      dispatchClientUpdated(payload);
    });
    socket.on('order_updated', (payload: ClientOrdersUpdatedDetail) => {
      if (payload?.orderId) {
        dispatchClientOrdersUpdated(payload);
        dispatchTenantOrdersUpdated({ orderId: payload.orderId, status: payload.status });
      }
    });
    socket.on('order_created', () => {
      dispatchTenantOrdersUpdated();
    });
    socket.on('announcement_created', () => {
      dispatchAnnouncementsUpdated();
    });
    socket.on('announcement_deleted', () => {
      dispatchAnnouncementsUpdated({ deleted: true });
    });
    socket.on('tenant_plan_updated', () => {
      dispatchTenantPlanUpdated();
    });
    socket.on('gallery_updated', (payload: { galleryId?: string }) => {
      if (payload?.galleryId) dispatchGalleryUpdated(payload.galleryId);
    });
    socket.on('gallery_selection_created', () => {
      dispatchGallerySelectionCreated();
    });
    socket.on('connect', () => {
      if (socket) joinRooms(socket);
    });
  }
  return socket;
}

/** Join tenant_public (+ client room when logged in as client, admin when staff). */
export function connectStorefrontSocket(
  tenantId: string,
  options?: { clientId?: string | null; joinAdmin?: boolean },
): void {
  activeTenantId = tenantId;
  activeClientId = options?.clientId ?? null;
  activeJoinAdmin = options?.joinAdmin === true;
  const s = ensureSocket();
  if (s.connected) {
    joinRooms(s);
  } else {
    s.connect();
  }
}

export function disconnectStorefrontSocket(): void {
  activeTenantId = null;
  activeClientId = null;
  activeJoinAdmin = false;
  if (!socket) return;
  socket.disconnect();
  socket.removeAllListeners();
  socket = null;
}
